<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { DirectUpload } from 'activestorage';
import {
  getAllowedFileTypesByChannel,
  getMaxUploadSizeByChannel,
} from '@chatwoot/utils';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import {
  DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  conversationId: {
    type: [Number, String],
    default: undefined,
  },
  inboxId: {
    type: [Number, String],
    default: undefined,
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();

const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const inboxes = useMapGetter('inboxes/getInboxes');
const globalConfig = useMapGetter('globalConfig/get');

const processedParams = ref({});
const includeIungoMedia = ref(false);
const iungoMedia = ref({
  type: 'image',
  url: '',
  name: '',
});

const headerAttachment = ref(null);

const inbox = computed(() =>
  inboxes.value?.find(r => r.id === Number(props.inboxId))
);

const effectiveConversationId = computed(() => {
  const raw = props.conversationId;
  if (raw === undefined || raw === null || raw === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
});

const headerFileInputRef = ref(null);

const openHeaderFilePicker = () => {
  headerFileInputRef.value?.click?.();
};

const installationLimit = computed(() =>
  resolveMaximumFileUploadSize(globalConfig.value?.maximumFileUploadSize)
);

const maxSizeForMime = mime => {
  const channelType = inbox.value?.channel_type;

  if (!channelType || channelType === INBOX_TYPES.WEB) {
    return installationLimit.value;
  }

  const channelLimit = getMaxUploadSizeByChannel({
    channelType,
    medium: inbox.value?.medium,
    mime,
  });

  if (channelLimit === DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE) {
    return installationLimit.value;
  }

  return Math.min(channelLimit, installationLimit.value);
};

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format?.toUpperCase?.())
);

const localizedHeaderFormatLabel = computed(() => {
  const fmt = headerComponent.value?.format?.toUpperCase();
  switch (fmt) {
    case 'IMAGE':
      return t('WHATSAPP_TEMPLATES.PARSER.MEDIA_TYPE_IMAGE');
    case 'VIDEO':
      return t('WHATSAPP_TEMPLATES.PARSER.MEDIA_TYPE_VIDEO');
    case 'DOCUMENT':
      return t('WHATSAPP_TEMPLATES.PARSER.MEDIA_TYPE_DOCUMENT');
    default:
      return headerComponent.value?.format || '';
  }
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const isIungoTemplate = computed(() => props.template.provider === 'iungo');

// Iungo send path reads header media from processed_params; the extra "attach media"
// block is only for templates without a media header (legacy / edge cases).
const shouldShowIungoMediaFields = computed(
  () => isIungoTemplate.value && !hasMediaHeader.value
);

const isIungoDocumentMedia = computed(
  () => iungoMedia.value.type === 'document'
);

const renderedTemplate = computed(() => {
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

const canUploadHeaderMedia = computed(() => hasMediaHeader.value);

const iungoHeaderMediaHint = computed(() =>
  canUploadHeaderMedia.value
    ? t('WHATSAPP_TEMPLATES.PARSER.IUNGO_HEADER_MEDIA_HINT')
    : t('WHATSAPP_TEMPLATES.PARSER.IUNGO_HEADER_MEDIA_HINT_URL_ONLY')
);

const headerMediaAccept = computed(() => {
  const fmt = headerComponent.value?.format?.toUpperCase();
  if (fmt === 'IMAGE') return 'image/*';
  if (fmt === 'VIDEO') return 'video/*';
  if (fmt === 'DOCUMENT') {
    return getAllowedFileTypesByChannel({
      channelType: inbox.value?.channel_type,
      medium: inbox.value?.medium,
    });
  }
  return undefined;
});

const hasHeaderMediaSource = computed(
  () =>
    Boolean(processedParams.value.header?.media_url?.trim()) ||
    Boolean(headerAttachment.value)
);

const isFormInvalid = computed(() => {
  if (!hasVariables.value && !hasMediaHeader.value) return false;

  if (hasMediaHeader.value && !hasHeaderMediaSource.value) {
    return true;
  }

  if (hasVariables.value && processedParams.value.body) {
    const hasEmptyBodyVariable = Object.values(processedParams.value.body).some(
      value => !value
    );
    if (hasEmptyBodyVariable) return true;
  }

  if (processedParams.value.buttons) {
    const hasEmptyButtonParameter = processedParams.value.buttons.some(
      button => !button.parameter
    );
    if (hasEmptyButtonParameter) return true;
  }

  if (
    shouldShowIungoMediaFields.value &&
    includeIungoMedia.value &&
    !iungoMedia.value.url
  ) {
    return true;
  }

  return false;
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
  if (value?.trim()) {
    headerAttachment.value = null;
  }
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

const onHeaderFileChosen = fileWrapper => {
  if (!canUploadHeaderMedia.value || !fileWrapper?.file) return;

  const cid = effectiveConversationId.value;

  const mime = fileWrapper.file?.type || fileWrapper.type;
  const maxSizeMB = maxSizeForMime(mime);

  if (!checkFileSizeLimit(fileWrapper, maxSizeMB)) {
    useAlert(
      t('CONVERSATION.FILE_SIZE_LIMIT', {
        MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: maxSizeMB,
      })
    );
    return;
  }

  if (globalConfig.value.directUploadsEnabled && cid) {
    const upload = new DirectUpload(
      fileWrapper.file,
      `/api/v1/accounts/${accountId.value}/conversations/${cid}/direct_uploads`,
      {
        directUploadWillCreateBlobWithXHR: xhr => {
          xhr.setRequestHeader(
            'api_access_token',
            currentUser.value.access_token
          );
        },
      }
    );

    upload.create((error, blob) => {
      if (error) {
        useAlert(error);
      } else {
        headerAttachment.value = {
          blobSignedId: blob.signed_id,
          name: fileWrapper.name || fileWrapper.file?.name,
        };
        updateMediaUrl('');
      }
    });
  } else {
    headerAttachment.value = {
      fileForUpload: fileWrapper.file,
      name: fileWrapper.name || fileWrapper.file?.name,
    };
    updateMediaUrl('');
  }
};

const onHeaderFileInputChange = event => {
  const input = event.target;
  const file = input?.files?.[0];
  if (input) {
    input.value = '';
  }
  if (!file) return;
  onHeaderFileChosen({ file, name: file.name, type: file.type });
};

const clearHeaderAttachment = () => {
  headerAttachment.value = null;
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const { name, category, language, namespace } = props.template;

  const payload = {
    message: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: processedParams.value,
    },
  };

  if (headerAttachment.value) {
    const { blobSignedId, fileForUpload } = headerAttachment.value;
    if (blobSignedId) {
      payload.files = [blobSignedId];
    } else if (fileForUpload) {
      payload.files = [fileForUpload];
    }
  }

  if (shouldShowIungoMediaFields.value && includeIungoMedia.value) {
    payload.templateParams.media = {
      type: iungoMedia.value.type,
      url: iungoMedia.value.url,
      ...(isIungoDocumentMedia.value && iungoMedia.value.name
        ? { name: iungoMedia.value.name }
        : {}),
    };
  }

  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    includeIungoMedia.value = false;
    iungoMedia.value = {
      type: 'image',
      url: '',
      name: '',
    };
    headerAttachment.value = null;
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedTemplate,
  v$,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: localizedHeaderFormatLabel,
            })
          }}
        </p>
        <div class="flex flex-col gap-2">
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            class="flex-1 w-full"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: localizedHeaderFormatLabel,
              })
            "
            @update:model-value="updateMediaUrl"
          />
          <div v-if="canUploadHeaderMedia" class="flex flex-col gap-2">
            <p class="text-xs text-n-slate-11">
              {{ t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_OR_UPLOAD') }}
            </p>
            <div class="flex flex-wrap items-center gap-2">
              <input
                ref="headerFileInputRef"
                type="file"
                class="hidden"
                :accept="headerMediaAccept || undefined"
                @change="onHeaderFileInputChange"
              />
              <Button
                type="button"
                color="slate"
                variant="faded"
                size="sm"
                :label="t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_UPLOAD')"
                @click="openHeaderFilePicker"
              />
              <span
                v-if="headerAttachment?.name"
                class="text-xs text-n-slate-12 truncate max-w-[12rem]"
              >
                {{ headerAttachment.name }}
              </span>
              <Button
                v-if="headerAttachment"
                type="button"
                color="slate"
                variant="faded"
                size="sm"
                :label="t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_CLEAR_FILE')"
                @click="clearHeaderAttachment"
              />
            </div>
          </div>
        </div>
        <p
          v-if="isIungoTemplate"
          class="mb-2.5 mt-2 text-xs leading-relaxed text-n-slate-11"
        >
          {{ iungoHeaderMediaHint }}
        </p>
        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <div v-if="shouldShowIungoMediaFields" class="mb-4">
        <div class="mb-2 flex items-center gap-2">
          <input
            id="iungo-template-media"
            v-model="includeIungoMedia"
            type="checkbox"
          />
          <label for="iungo-template-media" class="text-sm font-semibold">
            {{ t('WHATSAPP_TEMPLATES.PARSER.IUNGO_MEDIA_ENABLE') }}
          </label>
        </div>

        <div v-if="includeIungoMedia" class="space-y-2">
          <select
            v-model="iungoMedia.type"
            class="w-full py-2 px-3 rounded-md border border-n-slate-6 bg-n-background text-n-slate-12"
          >
            <option value="image">
              {{ t('WHATSAPP_TEMPLATES.PARSER.MEDIA_TYPE_IMAGE') }}
            </option>
            <option value="video">
              {{ t('WHATSAPP_TEMPLATES.PARSER.MEDIA_TYPE_VIDEO') }}
            </option>
            <option value="document">
              {{ t('WHATSAPP_TEMPLATES.PARSER.MEDIA_TYPE_DOCUMENT') }}
            </option>
          </select>

          <Input
            v-model="iungoMedia.url"
            type="url"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.IUNGO_MEDIA_URL')"
          />

          <Input
            v-if="isIungoDocumentMedia"
            v-model="iungoMedia.name"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
